import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ContactType, Customer, SearchCriteria } from '@raptor-fe/core';
import { ServiceUtils } from '../service-utils/service-utils';
import { LoadingHeaders } from '../web-loading-service/web-loading-headers';

@Injectable({
    providedIn: 'root',
})
export class CustomerApiService {
    private readonly http = inject(HttpClient);
    private readonly serviceUtils = inject(ServiceUtils);
    private httpHeadersWithNoLoading = LoadingHeaders.httpHeadersWithNoLoading();

    delete(tenantId: string, id: string): Observable<object> {
        return this.http.delete(this.serviceUtils.getBaseUrl('customer', tenantId) + `/${id}`);
    }

    deleteVehicleFromCustomer(tenantId: string, id: string, vehicleId: string): Observable<object> {
        return this.http.delete(`${this.serviceUtils.getBaseUrl('customer', tenantId)}/${id}/vehicle/${vehicleId}`);
    }

    get(tenantId: string, id: string | null): Observable<Customer | null> {
        if (!id || id === null) return of(<Customer>{ id: null, tenantId: tenantId });

        return this.http.get<Customer>(this.serviceUtils.getBaseUrl('customer', tenantId) + `/${id}`);
    }

    getContactTypes(tenantId: string): Observable<ContactType[]> {
        return this.http.get<ContactType[]>(this.serviceUtils.getBaseUrl('contactType', tenantId));
    }

    save(tenantId: string, customer: Customer): Observable<Customer> {
        if (!customer.id || customer.id === null) return this.http.post<Customer>(this.serviceUtils.getBaseUrl('customer', tenantId), customer);
        else return this.http.put<Customer>(this.serviceUtils.getBaseUrl('customer', tenantId), customer);
    }

    search(tenantId: string, criteria: SearchCriteria, showLoading = true): Observable<Customer[]> {
        return this.http.post<Customer[]>(this.serviceUtils.getBaseUrl('customer', tenantId) + `/search`, criteria, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    searchByKeyword(
        tenantId: string,
        keyword: string,
        pageNumber: number,
        pageSize: number,
        searchType: 'Customer' | 'All' | 'Vehicle' = 'Customer',
        showLoading = true,
    ): Observable<Customer[]> {
        const body = { pageSize, pageNumber, items: [], orderBy: 'name' };
        let url = this.serviceUtils.getBaseUrl('customer', tenantId) + `/search/keyword`;

        if (keyword && keyword.length > 0) url += `/${encodeURIComponent(keyword)}`;

        url += `?searchType=${searchType}`;

        return this.http.post<Customer[]>(url, body, {
            headers: showLoading ? undefined : this.httpHeadersWithNoLoading,
        });
    }

    sync(tenantId: string): Observable<object> {
        return this.http.post(this.serviceUtils.getBaseUrl('customer', tenantId) + `/sync`, {});
    }
}
